import { c as _c } from "react/compiler-runtime";
import { Form, LoadingSpinner, PageShell, SuccessCheck } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { renderScrubbedText } from './render-scrubbed-text';
import { ResendButton } from './resend-button';
type ErrorCallback = (message: string) => void;
type ChallengeKind = 'sms' | 'email';
type TFn = ReturnType<typeof useTranslation<'idv2'>>['t'];
const getTitle = (t: TFn, kind: ChallengeKind, isReturningUser: boolean): string => {
  if (isReturningUser) return t($ => $.identify.otp.welcomeBack);
  return t($ => $.identify.otp[kind].title);
};
const getSubtitle = (t: TFn, kind: ChallengeKind, contactInfo?: string): string => {
  if (contactInfo && kind === 'email') return t($ => $.identify.otp.email.subtitleWithEmail, {
    email: contactInfo
  });
  if (contactInfo && kind === 'sms') return t($ => $.identify.otp.sms.subtitleWithPhone, {
    phone: contactInfo
  });
  return t($ => $.identify.otp[kind].subtitle);
};
const getSubtitleNode = (subtitle: string, challengeKind: ChallengeKind | undefined, contactInfo?: string): ReactNode => {
  if (!contactInfo || !challengeKind) return renderScrubbedText(subtitle);
  const actionName = challengeKind === 'email' ? 'Subtitle with email' : 'Subtitle with phone';
  return <span data-dd-privacy="mask" data-dd-action-name={actionName}>
      {renderScrubbedText(subtitle)}
    </span>;
};
export type OtpChallengeProps = {
  challengeKind?: ChallengeKind;
  contactInfo?: string;
  hint?: string;
  isReturningUser?: boolean;
  onSubmit?: (challengeResponse: string, onError: ErrorCallback) => void;
  onClose?: () => void;
  onResend?: () => void;
  isLoading?: boolean;
  isResendLoading?: boolean;
  isVerifying?: boolean;
  isSuccess?: boolean;
  resendDisabledUntil?: Date;
  showLegalFooter?: boolean;
};
type OtpChallengeFormData = {
  otp: string;
};
export const OtpChallenge = t0 => {
  const $ = _c(62);
  const {
    challengeKind,
    contactInfo,
    hint,
    isReturningUser: t1,
    onSubmit,
    isLoading: t2,
    isResendLoading: t3,
    isVerifying: t4,
    isSuccess: t5,
    onResend,
    resendDisabledUntil,
    showLegalFooter: t6
  } = t0;
  const isReturningUser = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const isResendLoading = t3 === undefined ? false : t3;
  const isVerifying = t4 === undefined ? false : t4;
  const isSuccess = t5 === undefined ? false : t5;
  const showLegalFooter = t6 === undefined ? true : t6;
  const {
    t
  } = useTranslation("idv2");
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      defaultValues: {
        otp: ""
      }
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const methods = useForm(t7);
  let t8;
  if ($[1] !== methods || $[2] !== onSubmit) {
    t8 = value => {
      onSubmit?.(value, message => {
        methods.setError("otp", {
          message
        });
        methods.setValue("otp", "");
      });
    };
    $[1] = methods;
    $[2] = onSubmit;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const handleComplete = t8;
  const kind = challengeKind ?? "sms";
  let t9;
  if ($[4] !== isReturningUser || $[5] !== kind || $[6] !== t) {
    t9 = getTitle(t, kind, isReturningUser);
    $[4] = isReturningUser;
    $[5] = kind;
    $[6] = t;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const title = t9;
  let t10;
  if ($[8] !== challengeKind || $[9] !== contactInfo || $[10] !== kind || $[11] !== t) {
    const subtitle = getSubtitle(t, kind, contactInfo);
    t10 = getSubtitleNode(subtitle, challengeKind, contactInfo);
    $[8] = challengeKind;
    $[9] = contactInfo;
    $[10] = kind;
    $[11] = t;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const subtitleNode = t10;
  if (isSuccess) {
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <SuccessCheck animationStart={true} size={32} />;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t) {
      t12 = t(_temp);
      $[14] = t;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    let t13;
    if ($[16] !== t12) {
      t13 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t11}<p className="text-label-2 text-success fp-text">{t12}</p></div>;
      $[16] = t12;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    let t14;
    if ($[18] !== subtitleNode || $[19] !== t13 || $[20] !== title) {
      t14 = <PageShell title={title} subtitle={subtitleNode}>{t13}</PageShell>;
      $[18] = subtitleNode;
      $[19] = t13;
      $[20] = title;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    return t14;
  }
  if (isVerifying) {
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex justify-center items-center h-8"><LoadingSpinner size="xlarge" /></div>;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== t) {
      t12 = t(_temp2);
      $[23] = t;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== t12) {
      t13 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t11}<p className="text-label-2 text-primary fp-text">{t12}</p></div>;
      $[25] = t12;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    let t14;
    if ($[27] !== subtitleNode || $[28] !== t13 || $[29] !== title) {
      t14 = <PageShell title={title} subtitle={subtitleNode}>{t13}</PageShell>;
      $[27] = subtitleNode;
      $[28] = t13;
      $[29] = title;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    return t14;
  }
  let t11;
  if ($[31] !== t) {
    t11 = t(_temp3);
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11) {
    t12 = {
      required: t11
    };
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== handleComplete || $[36] !== isLoading || $[37] !== t12) {
    t13 = <Form.Otp name="otp" rules={t12} disabled={isLoading} onComplete={handleComplete} autoFocus={true} />;
    $[35] = handleComplete;
    $[36] = isLoading;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== hint) {
    t14 = hint && <p className="text-body-2 text-secondary text-center w-full">{hint}</p>;
    $[39] = hint;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== isLoading) {
    t15 = isLoading && <LoadingSpinner className="min-h-[20px]" />;
    $[41] = isLoading;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t15) {
    t16 = <div className="w-full flex flex-col items-center gap-2">{t13}{t14}{t15}</div>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== isResendLoading || $[48] !== onResend || $[49] !== resendDisabledUntil || $[50] !== showLegalFooter) {
    t17 = onResend && <ResendButton onResend={onResend} isResendLoading={isResendLoading} resendDisabledUntil={resendDisabledUntil} showLegalFooter={showLegalFooter} />;
    $[47] = isResendLoading;
    $[48] = onResend;
    $[49] = resendDisabledUntil;
    $[50] = showLegalFooter;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t16 || $[53] !== t17) {
    t18 = <div className="flex flex-col gap-6 items-center w-full">{t16}{t17}</div>;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== methods || $[56] !== t18) {
    t19 = <FormProvider {...methods}>{t18}</FormProvider>;
    $[55] = methods;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== subtitleNode || $[59] !== t19 || $[60] !== title) {
    t20 = <PageShell title={title} subtitle={subtitleNode}>{t19}</PageShell>;
    $[58] = subtitleNode;
    $[59] = t19;
    $[60] = title;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
};
export default OtpChallenge;
function _temp($) {
  return $.identify.otp.success;
}
function _temp2($_0) {
  return $_0.identify.otp.verifying;
}
function _temp3($_1) {
  return $_1.identify.otp.errors.invalidCode;
}